import React, { useContext, useState } from 'react'
import FloristSearchUI from '@interflora/ui-components/build/components/FloristSearch/FloristSearch'
import { findLocation } from 'lib/AzureMapsAPI'
import { SiteContext, getCountryFromAlpha2Code, CountryCodes } from '@interflora/ui-components/build/utils/common'
import algoliasearch from 'algoliasearch'
import { default as FloristSearchGalleryUI } from '@interflora/ui-components/build/components/FloristSearchGallery/FloristSearchGallery'
import { FLORIST_GEO_SEARCH_RADIUS_METERS } from '../../constants'
import { useLoaded } from '@interflora/ui-components/build/components/MainContent/useComponentLoading'

const envPrefix = process.env.NEXT_PUBLIC_ALGOLIA_ENV_PREFIX
const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY
)

type Props = {
  onLoaded?: () => void // Optional callback for component loading
}

const FloristSearch: React.FC<Props> = ({ onLoaded }) => {
  const { shorthand, countryCode } = useContext(SiteContext)
  const [coordinates, setCoordinates] = useState([])
  const [pageNo, setPageNo] = useState(0)
  const [florists, setFlorists] = useState([])
  const HITS_PER_PAGE = 6
  const [hasMore, setHasMore] = useState(false)
  const countryBasedOnSite = getCountryFromAlpha2Code(countryCode as string)
  const index = searchClient.initIndex(`${envPrefix}_florist_${shorthand}`)

  const findFlorists = async (values) => {
    setPageNo(0)
    try {
      const floristResult = await findLocation({ location: values.postcode })
      const latLongValues = floristResult?.data.results.filter(
        (resource) => resource.address.country.toLowerCase() === countryBasedOnSite.name.toLowerCase()
      )[0]?.position
      const coordinates = [latLongValues.lat, latLongValues.lon]
      setCoordinates(coordinates)
      const floristsResults = await algoliaSearch(0, coordinates)
      if (!!floristsResults?.length) {
        setFlorists(floristsResults)
      } else {
        throw new Error(`We can’t find any florists local to "${values.postcode}"`)
      }
    } catch (error) {
      setFlorists([])
      setCoordinates([])
      setHasMore(false)
      throw new Error(`We can’t find any florists local to "${values.postcode}"`)
    }
  }

  const algoliaSearch = async (pageNo: number, coordinates: any) => {
    if (coordinates?.length === 0) {
      return []
    }
    const algoliaSearchResults = await index.search('', {
      aroundLatLng: `${coordinates?.join(',')}`,
      aroundRadius: FLORIST_GEO_SEARCH_RADIUS_METERS,
      hitsPerPage: HITS_PER_PAGE,
      page: pageNo,
      distinct: true,
      getRankingInfo: true,
    })
    const { hitsPerPage, hits, nbHits, page } = algoliaSearchResults
    const floristDetails = hits?.map((hit: any) => {
      const distanceUnit = countryCode === CountryCodes.UNITED_KINDOM ? 'mi' : 'km'
      const distanceUnitConversionFactor = distanceUnit === 'mi' ? 1609 : 1000
      const distance = hit?._rankingInfo?.geoDistance
        ? `${(hit._rankingInfo.geoDistance / distanceUnitConversionFactor).toFixed(2)}${distanceUnit}`
        : ''
      return {
        ...hit.data,
        distance,
      }
    })
    const totalViewed = hits.length === hitsPerPage ? (page + 1) * hitsPerPage : nbHits
    setHasMore(totalViewed < nbHits)
    return floristDetails
  }

  const onLoadMore = async () => {
    const newPageNo = pageNo + 1
    setPageNo(newPageNo)
    const floristsResults = await algoliaSearch(newPageNo, coordinates)
    setFlorists(florists.concat(floristsResults))
  }

  useLoaded({ onLoaded })
  return (
    <>
      <FloristSearchUI onSubmit={findFlorists} />
      <FloristSearchGalleryUI onLoadMore={onLoadMore} floristSearchResults={florists} hasMore={hasMore} />
    </>
  )
}

export default FloristSearch
